import { useState } from 'react';

export interface TileCategory {
  key: string;
  label: string;
  tiles: string[];
}

/**
 * Same persistence contract as `useTileOrder`, but scoped per-category so drag-and-drop
 * reordering stays confined WITHIN a category's own row (matching the client's PDF spec —
 * category + tile name grouping is fixed, only the order of tiles inside a category can move).
 * Persists all categories' orders as one JSON object under a single localStorage key.
 */
export function useCategoryTileOrder(
  storageKey: string,
  categories: TileCategory[]
): [Record<string, string[]>, (categoryKey: string, newOrder: string[]) => void, () => void] {
  const defaultOrders = (): Record<string, string[]> => {
    const m: Record<string, string[]> = {};
    categories.forEach((c) => { m[c.key] = c.tiles; });
    return m;
  };

  const [orders, setOrders] = useState<Record<string, string[]>>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as Record<string, string[]>;
        const valid = categories.every((c) => {
          const savedIds = parsed[c.key];
          return Array.isArray(savedIds) && savedIds.length === c.tiles.length && c.tiles.every((id) => savedIds.includes(id));
        });
        if (valid) return parsed;
      }
    } catch {
      // ignore corrupt localStorage value, fall back to default
    }
    return defaultOrders();
  });

  const reorder = (categoryKey: string, newOrder: string[]) => {
    setOrders((prev) => {
      const next = { ...prev, [categoryKey]: newOrder };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const resetAll = () => {
    const d = defaultOrders();
    setOrders(d);
    localStorage.removeItem(storageKey);
  };

  return [orders, reorder, resetAll];
}
